/**
 * SRES · Cockpit → Captured Views Gallery panel (Iter98d · Feb 2026).
 *
 * Right-side drawer that surfaces every captured 3D view for the
 * current project so users can review, re-title or delete them
 * without leaving the cockpit. Reads from the existing
 * `/api/projects/{pid}/block-model/captured-view*` endpoints.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, PencilSimple, Trash, Check, Image } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, API_BASE } from "@/lib/api";


function ViewCard({ pid, v, onDeleted, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(v.caption || "");

  const rename = useMutation({
    mutationFn: async (caption) => {
      const fd = new FormData();
      fd.append("caption", caption);
      return api.patch(`/projects/${pid}/block-model/captured-view/${v.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: () => {
      toast.success("Caption updated");
      setEditing(false);
      onRenamed?.();
    },
    onError: () => toast.error("Failed to update caption"),
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/projects/${pid}/block-model/captured-view/${v.id}`),
    onSuccess: () => {
      toast.success("View deleted");
      onDeleted?.();
    },
    onError: () => toast.error("Failed to delete view"),
  });

  // Iter98e · Approve-for-CPR toggle. Backend already accepts an
  // `approved` bool on the PATCH endpoint; flipping it decides whether
  // this captured view is embedded into CPR Appendix Y on the next
  // compile. Default (from upload) is `true`.
  const setApproved = useMutation({
    mutationFn: async (approved) => {
      const fd = new FormData();
      fd.append("approved", approved ? "true" : "false");
      return api.patch(`/projects/${pid}/block-model/captured-view/${v.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: (_, approved) => {
      toast.success(approved
        ? "Included in CPR Appendix Y."
        : "Excluded from CPR Appendix Y.");
      onRenamed?.();  // reuses gallery refresh
    },
    onError: () => toast.error("Failed to update approval flag"),
  });

  const thumbUrl = `${API_BASE}/projects/${pid}/block-model/captured-view/${v.id}/thumb.jpg`;
  const ts = String(v.created_at || "").slice(0, 19).replace("T", " ");

  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden"
           data-testid={`cockpit-gallery-item-${v.id.slice(0, 8)}`}>
      <a href={`${API_BASE}/projects/${pid}/block-model/captured-view/${v.id}/full.png`}
           target="_blank" rel="noreferrer"
           className="block">
        <img src={thumbUrl} alt={v.caption || "Captured view"}
                className="w-full h-24 object-cover bg-slate-100"
                crossOrigin="use-credentials" />
      </a>
      <div className="px-2 py-1.5">
        {editing ? (
          <div className="flex items-center gap-1">
            <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                     className="h-6 text-[11px] px-1.5" autoFocus
                     onKeyDown={(e) => {
                       if (e.key === "Enter") rename.mutate(draft);
                       if (e.key === "Escape") setEditing(false);
                     }}
                     data-testid={`cockpit-gallery-caption-input-${v.id.slice(0, 8)}`} />
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                       onClick={() => rename.mutate(draft)}
                       disabled={rename.isPending}
                       data-testid={`cockpit-gallery-caption-save-${v.id.slice(0, 8)}`}>
              <Check size={11} />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <div className="flex-1 text-[11.5px] font-medium text-slate-800 truncate"
                    title={v.caption || "(no caption)"}>
              {v.caption || <i className="text-slate-400">no caption</i>}
            </div>
            <Button size="sm" variant="ghost" className="h-5 w-5 p-0 shrink-0"
                       onClick={() => { setDraft(v.caption || ""); setEditing(true); }}
                       data-testid={`cockpit-gallery-rename-${v.id.slice(0, 8)}`}>
              <PencilSimple size={10} />
            </Button>
          </div>
        )}
        <div className="mt-0.5 flex items-center gap-1.5">
          <div className="text-[10px] text-slate-500 font-mono flex-1 truncate">{ts}</div>
          <div className="text-[9.5px] text-slate-400 uppercase tracking-widest">{v.colour_mode || "—"}</div>
          <Button size="sm" variant="ghost"
                     className="h-5 w-5 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                     onClick={() => {
                       if (window.confirm("Delete this captured view?")) del.mutate();
                     }}
                     disabled={del.isPending}
                     data-testid={`cockpit-gallery-delete-${v.id.slice(0, 8)}`}>
            <Trash size={10} />
          </Button>
        </div>
        <div className="mt-1 flex items-center justify-between gap-1.5 pt-1 border-t border-slate-100">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${v.approved !== false ? "text-emerald-700" : "text-slate-400"}`}>
            {v.approved !== false ? "In CPR Appendix Y" : "Excluded from CPR"}
          </span>
          <Switch
            checked={v.approved !== false}
            onCheckedChange={(next) => setApproved.mutate(next)}
            disabled={setApproved.isPending}
            data-testid={`cockpit-gallery-approve-${v.id.slice(0, 8)}`}
            className="scale-75 origin-right"
          />
        </div>
      </div>
    </div>
  );
}


export function CapturedViewsGallery({ pid, open, onClose }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["captured-views", pid],
    queryFn:  () => api.get(`/projects/${pid}/block-model/captured-views`).then((r) => r.data),
    enabled:  !!pid && open,
    staleTime: 5_000,
  });
  if (!open) return null;
  const views = q.data?.views || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["captured-views", pid] });

  return (
    <div className="absolute right-0 top-0 bottom-0 w-80 bg-white/95 backdrop-blur border-l border-slate-200 shadow-xl flex flex-col z-30"
           data-testid="cockpit-gallery">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
        <Image size={12} weight="duotone" className="text-brand-blue" />
        <div className="flex-1 text-[11.5px] font-bold uppercase tracking-widest text-slate-700">
          Captured Views {views.length > 0 && <span className="text-slate-400">({views.length})</span>}
        </div>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                   onClick={onClose} data-testid="cockpit-gallery-close">
          <X size={12} />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {q.isLoading ? (
          <div className="animate-pulse space-y-2">
            {Array.from({ length: 3 }).map((_, i) =>
              <div key={i} className="h-32 bg-slate-100 rounded" />)}
          </div>
        ) : views.length === 0 ? (
          <div className="text-center text-slate-500 py-8 text-[12px]"
                 data-testid="cockpit-gallery-empty">
            No captured views yet.
            <div className="mt-1 text-[10.5px] text-slate-400">
              Use <b>Capture View</b> above to save the current camera pose into the CPR Appendix Y library.
            </div>
          </div>
        ) : (
          views.map((v) => (
            <ViewCard key={v.id} pid={pid} v={v}
                        onDeleted={refresh} onRenamed={refresh} />
          ))
        )}
      </div>
    </div>
  );
}

export default CapturedViewsGallery;
